import React, { useState } from 'react';
import { X, BookOpen, MessageCircle, Send, Lock, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

/**
 * GrammarOverlay - Floating overlay showing grammar explanation
 * 
 * Props:
 * - topic: Grammar topic object with name, description, examples, etc.
 * - verseContext: Optional verse text for context
 * - onClose: Callback to close the overlay
 * - language: 'hebrew' or 'greek'
 */
const GrammarOverlay = ({ topic, verseContext, onClose, language = 'hebrew' }) => {
  const { user } = useAuth();
  const [question, setQuestion] = useState('');
  const [aiResponse, setAiResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const userTier = user?.tier || 0;
  const canAskAI = userTier >= 2;

  const handleAskAI = async () => {
    if (!question.trim() || !canAskAI) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/grammar/ask', {
        topic_id: topic.id,
        question: question.trim(),
        language: language,
        context: verseContext
      });

      setAiResponse(response.data);
    } catch (err) {
      console.error('Failed to get AI response:', err);
      setError(err.response?.data?.detail || 'Failed to get response');
    } finally {
      setLoading(false);
    }
  };

  if (!topic) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div 
        className="bg-slate-800 rounded-xl border border-slate-700 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-slate-900/50">
          <div className="flex items-center gap-3">
            <BookOpen className="w-6 h-6 text-amber-500" />
            <div>
              <h2 className="text-lg font-semibold text-white">{topic.name}</h2>
              {topic.hebrew_name && (
                <span className="text-amber-400 font-hebrew text-sm">{topic.hebrew_name}</span>
              )}
              {topic.greek_name && (
                <span className="text-purple-400 text-sm">{topic.greek_name}</span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            data-testid="close-grammar-overlay"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Description */}
          <div>
            <p className="text-slate-300 leading-relaxed">{topic.description}</p>
            {topic.meaning && (
              <span className="inline-block mt-2 text-sm px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full">
                {topic.meaning}
              </span>
            )}
          </div>

          {/* Verse Context */}
          {verseContext && (
            <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <h3 className="text-sm font-semibold text-slate-400 mb-2">From this verse:</h3>
              <p className={`text-lg ${language === 'hebrew' ? 'font-hebrew text-right' : ''} text-white`}
                 dir={language === 'hebrew' ? 'rtl' : 'ltr'}>
                {verseContext}
              </p>
            </div>
          )}

          {/* Examples */}
          {topic.examples && topic.examples.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                <ChevronRight className="w-4 h-4 text-amber-500" />
                Examples
              </h3>
              <div className="space-y-2">
                {topic.examples.map((example, i) => (
                  <div key={i} className="bg-slate-900/30 rounded-lg p-3 border-l-2 border-amber-500">
                    <p className={`${language === 'hebrew' ? 'font-hebrew text-right' : ''} text-slate-200`}
                       dir={language === 'hebrew' ? 'rtl' : 'ltr'}>
                      {example}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rules */}
          {topic.rules && topic.rules.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                <ChevronRight className="w-4 h-4 text-amber-500" />
                Key Rules
              </h3>
              <ul className="space-y-2">
                {topic.rules.map((rule, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-300">
                    <span className="text-green-400 mt-1 text-sm">✓</span>
                    <span>{rule}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Suffixes/Prefixes */}
          {(topic.suffixes || topic.prefixes) && (
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                <ChevronRight className="w-4 h-4 text-amber-500" />
                {topic.suffixes ? 'Person Suffixes' : 'Person Prefixes'}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(topic.suffixes || topic.prefixes || {}).map(([key, value]) => (
                  <div key={key} className="bg-slate-900/50 rounded-lg p-2 text-sm">
                    <span className="text-amber-400 font-medium">{key}:</span>{' '}
                    <span className="text-slate-300">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Response */}
          {aiResponse && (
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-purple-400 mb-2 flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                AI Response
              </h3>
              <p className="text-slate-300">{aiResponse.answer}</p>
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
              {error}
            </div>
          )}
        </div>

        {/* AI Q&A Section */}
        <div className="border-t border-slate-700 p-4 bg-slate-900/50">
          <h3 className="text-sm font-semibold text-slate-400 mb-3 flex items-center gap-2">
            <MessageCircle className="w-4 h-4" />
            Do you have any questions about this lesson?
          </h3>
          
          {canAskAI ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAskAI()}
                placeholder="Ask about this grammar concept..."
                className="flex-1 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
                disabled={loading}
              />
              <button
                onClick={handleAskAI}
                disabled={!question.trim() || loading}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 rounded-lg font-medium flex items-center gap-2 transition-colors"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-900 border-t-transparent"></div>
                ) : (
                  <Send className="w-4 h-4" />
                )}
                <span>Ask AI</span>
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 p-3 bg-slate-800 rounded-lg border border-slate-700">
              <Lock className="w-5 h-5 text-slate-500" />
              <div>
                <p className="text-slate-400 text-sm">AI Q&A requires Tier 2 or higher</p>
                <p className="text-slate-500 text-xs">Upgrade to ask questions about grammar</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GrammarOverlay;
